import axios from "axios";

const ADMIN_API_BASE_URL = "/ofc/leave/admin/holidays";
const PUBLIC_API_BASE_URL = "/ofc/leave/holidays";

// Configure axios defaults
axios.defaults.withCredentials = true;

export interface HolidayDto {
  id?: number;
  holidayDate: string;
  holidayTypeId: number;
  holidayTypeName?: string;
  description?: string;
  reason?: string;
  color?: string; // Hex color code for calendar display
  officeLocations: string[];
  createdByName?: string;
  createdAt?: string;
}

export interface WorkdayOverrideDto {
  id?: number;
  overrideDate: string;
  dayOfWeek: number;
  reason: string;
  color?: string; // Hex color code for calendar display
  officeLocations: string[];
  createdByName?: string;
  createdAt?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  error?: string;
}

class HolidayApiService {
  // ========== ADMIN ENDPOINTS ==========

  /**
   * Get all holidays (Admin only)
   */
  async getAllHolidays(): Promise<HolidayDto[]> {
    const response =
      await axios.get<ApiResponse<HolidayDto[]>>(ADMIN_API_BASE_URL);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to fetch holidays");
    }
    return response.data.data;
  }

  /**
   * Get holidays by date range (Admin only)
   */
  async getAdminHolidaysByDateRange(
    startDate: string,
    endDate: string,
  ): Promise<HolidayDto[]> {
    const response = await axios.get<ApiResponse<HolidayDto[]>>(
      `${ADMIN_API_BASE_URL}/range`,
      { params: { startDate, endDate } },
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to fetch holidays");
    }
    return response.data.data;
  }

  /**
   * Create a new holiday (Admin only)
   */
  async createHoliday(holidayDto: HolidayDto): Promise<HolidayDto> {
    const response = await axios.post<ApiResponse<HolidayDto>>(
      ADMIN_API_BASE_URL,
      holidayDto,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to create holiday");
    }
    return response.data.data;
  }

  /**
   * Create multiple holidays in bulk (Admin only)
   * Sends a single consolidated notification for all holidays
   */
  async createHolidaysBulk(holidayDtos: HolidayDto[]): Promise<HolidayDto[]> {
    const response = await axios.post<ApiResponse<HolidayDto[]>>(
      `${ADMIN_API_BASE_URL}/bulk`,
      holidayDtos,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to bulk create holidays");
    }
    return response.data.data;
  }

  /**
   * Update an existing holiday (Admin only)
   */
  async updateHoliday(
    holidayId: number,
    holidayDto: HolidayDto,
  ): Promise<HolidayDto> {
    const response = await axios.put<ApiResponse<HolidayDto>>(
      `${ADMIN_API_BASE_URL}/${holidayId}`,
      holidayDto,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to update holiday");
    }
    return response.data.data;
  }

  /**
   * Delete a holiday (Admin only)
   */
  async deleteHoliday(holidayId: number): Promise<void> {
    const response = await axios.delete<ApiResponse<void>>(
      `${ADMIN_API_BASE_URL}/${holidayId}`,
    );
    if (!response.data.success) {
      throw new Error(response.data.error || "Failed to delete holiday");
    }
  }

  // ========== WORKDAY OVERRIDE ENDPOINTS ==========

  /**
   * Get all workday overrides (Admin only)
   */
  async getAllWorkdayOverrides(): Promise<WorkdayOverrideDto[]> {
    const response = await axios.get<ApiResponse<WorkdayOverrideDto[]>>(
      `${ADMIN_API_BASE_URL}/workday-overrides`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(
        response.data.error || "Failed to fetch workday overrides",
      );
    }
    return response.data.data;
  }

  /**
   * Create a new workday override (Admin only)
   */
  async createWorkdayOverride(
    workdayOverrideDto: WorkdayOverrideDto,
  ): Promise<WorkdayOverrideDto> {
    const response = await axios.post<ApiResponse<WorkdayOverrideDto>>(
      `${ADMIN_API_BASE_URL}/workday-overrides`,
      workdayOverrideDto,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(
        response.data.error || "Failed to create workday override",
      );
    }
    return response.data.data;
  }

  /**
   * Update an existing workday override (Admin only)
   */
  async updateWorkdayOverride(
    overrideId: number,
    workdayOverrideDto: WorkdayOverrideDto,
  ): Promise<WorkdayOverrideDto> {
    const response = await axios.put<ApiResponse<WorkdayOverrideDto>>(
      `${ADMIN_API_BASE_URL}/workday-overrides/${overrideId}`,
      workdayOverrideDto,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(
        response.data.error || "Failed to update workday override",
      );
    }
    return response.data.data;
  }

  /**
   * Delete a workday override (Admin only)
   */
  async deleteWorkdayOverride(overrideId: number): Promise<void> {
    const response = await axios.delete<ApiResponse<void>>(
      `${ADMIN_API_BASE_URL}/workday-overrides/${overrideId}`,
    );
    if (!response.data.success) {
      throw new Error(
        response.data.error || "Failed to delete workday override",
      );
    }
  }

  // ========== PUBLIC ENDPOINTS ==========

  /**
   * Get holidays by date range (Public)
   */
  async getPublicHolidaysByDateRange(
    startDate: string,
    endDate: string,
  ): Promise<HolidayDto[]> {
    const response = await axios.get<ApiResponse<HolidayDto[]>>(
      `${PUBLIC_API_BASE_URL}/range`,
      { params: { startDate, endDate } },
    );
    if (!response.data.success || !response.data.data) {
      return [];
    }
    return response.data.data;
  }

  /**
   * Check if a date is a workday
   */
  async isWorkday(date: string, officeLocation: string): Promise<boolean> {
    const response = await axios.get<ApiResponse<boolean>>(
      `${PUBLIC_API_BASE_URL}/is-workday`,
      { params: { date, officeLocation } },
    );
    if (!response.data.success) {
      return true; // Default to workday if check fails
    }
    return response.data.data;
  }

  /**
   * Get all non-workdays (holidays and rest days) for a date range
   */
  async getNonWorkdaysByDateRange(
    startDate: string,
    endDate: string,
    officeLocation: string,
  ): Promise<string[]> {
    try {
      const holidays = await this.getPublicHolidaysByDateRange(
        startDate,
        endDate,
      );
      const overrides = await this.getPublicWorkdayOverridesByDateRange(
        startDate,
        endDate,
      );

      const nonWorkdayDates: Set<string> = new Set();

      // Add holiday dates - filter by office location if provided
      holidays.forEach((h) => {
        let dateStr: string;
        if (Array.isArray(h.holidayDate)) {
          // Handle array format [year, month, day]
          const [year, month, day] = h.holidayDate;
          dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        } else {
          // Handle string format
          dateStr = h.holidayDate;
        }
        if (
          !officeLocation ||
          (h.officeLocations && h.officeLocations.includes(officeLocation))
        ) {
          nonWorkdayDates.add(dateStr);
        }
      });

      // Add weekends - parse dates as local dates (YYYY-MM-DD format)
      const [startYear, startMonth, startDay] = startDate
        .split("-")
        .map(Number);
      const [endYear, endMonth, endDay] = endDate.split("-").map(Number);

      const start = new Date(startYear, startMonth - 1, startDay);
      const end = new Date(endYear, endMonth - 1, endDay);
      let current = new Date(start);

      while (current <= end) {
        const dayOfWeek = current.getDay();
        if (dayOfWeek === 0 || dayOfWeek === 6) {
          // Sunday or Saturday
          const year = current.getFullYear();
          const month = String(current.getMonth() + 1).padStart(2, "0");
          const day = String(current.getDate()).padStart(2, "0");
          const dateStr = `${year}-${month}-${day}`;
          nonWorkdayDates.add(dateStr);
        }
        current.setDate(current.getDate() + 1);
      }

      // Remove workday override dates (rest days converted to workdays)
      // These dates should be AVAILABLE for leave applications
      overrides.forEach((o) => {
        let dateStr: string;
        if (Array.isArray(o.overrideDate)) {
          // Handle array format [year, month, day]
          const [year, month, day] = o.overrideDate;
          dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        } else {
          // Handle string format
          dateStr = o.overrideDate;
        }
        if (
          !officeLocation ||
          (o.officeLocations && o.officeLocations.includes(officeLocation))
        ) {
          // Remove from non-workdays because this rest day is now a working day
          nonWorkdayDates.delete(dateStr);
        }
      });

      return Array.from(nonWorkdayDates).sort();
    } catch (err) {
      console.error("Error getting non-workdays:", err);
      return [];
    }
  }

  /**
   * Get workday overrides by date range (Public)
   */
  private async getPublicWorkdayOverridesByDateRange(
    startDate: string,
    endDate: string,
  ): Promise<WorkdayOverrideDto[]> {
    const response = await axios.get<ApiResponse<WorkdayOverrideDto[]>>(
      `${PUBLIC_API_BASE_URL}/workday-overrides/range`,
      { params: { startDate, endDate } },
    );
    if (!response.data.success || !response.data.data) {
      return [];
    }
    return response.data.data;
  }

  /**
   * Get holidays by date range (Public - for Calendar page)
   */
  async getHolidaysByDateRange(
    startDate: string,
    endDate: string,
  ): Promise<HolidayDto[]> {
    return this.getPublicHolidaysByDateRange(startDate, endDate);
  }

  /**
   * Get workday overrides by date range (Public - for Calendar page)
   */
  async getWorkdayOverridesByDateRange(
    startDate: string,
    endDate: string,
  ): Promise<WorkdayOverrideDto[]> {
    return this.getPublicWorkdayOverridesByDateRange(startDate, endDate);
  }
}

export default new HolidayApiService();
